import { Button } from "@dream/ui";
import { Link } from "@tanstack/react-router";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type CookieConsentChoice,
  type CookieConsentRecord,
  makeCookieConsent,
  readCookieConsent,
  writeCookieConsent,
} from "../legal/consent.ts";

/**
 * Banner e preferência de cookies (LGPD).
 *
 * O aviso entra no fluxo do layout (`shrink-0` no rodapé da viewport), não
 * como overlay. O calendário, a pill e o login encolhem acima dele — nada
 * fica escondido atrás, e o `body` não ganha padding.
 */

interface CookieConsentContextValue {
  record: CookieConsentRecord | null;
  bannerOpen: boolean;
  decide: (choice: CookieConsentChoice) => void;
  reopen: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [record, setRecord] = useState<CookieConsentRecord | null>(null);
  const [bannerOpen, setBannerOpen] = useState(false);

  useEffect(() => {
    const stored = readCookieConsent();
    setRecord(stored);
    setBannerOpen(stored === null);
  }, []);

  const decide = useCallback((choice: CookieConsentChoice) => {
    const next = makeCookieConsent(choice);
    writeCookieConsent(next);
    setRecord(next);
    setBannerOpen(false);
  }, []);

  const reopen = useCallback(() => {
    setBannerOpen(true);
  }, []);

  const value = useMemo(
    () => ({ record, bannerOpen, decide, reopen }),
    [record, bannerOpen, decide, reopen],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent(): CookieConsentContextValue {
  const value = useContext(CookieConsentContext);
  if (value === null) {
    throw new Error("useCookieConsent precisa estar dentro de CookieConsentProvider");
  }
  return value;
}

export function CookieBanner() {
  const { bannerOpen, decide } = useCookieConsent();

  if (!bannerOpen) return null;

  return (
    <section
      data-testid="cookie-banner"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      className="shrink-0 border-t border-[var(--color-line)] bg-[var(--color-surface)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-8px_24px_rgb(0_0_0/0.08)]"
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 flex-1">
          <p
            id="cookie-banner-title"
            className="text-[0.8125rem] font-bold tracking-[0] text-[var(--color-ink)]"
          >
            Cookies e privacidade
          </p>
          <p
            id="cookie-banner-desc"
            className="mt-1 text-[0.75rem] leading-5 text-[var(--color-ink-secondary)]"
          >
            Usamos cookies necessários para autenticar a equipe e lembrar o tema. Não usamos cookies
            de publicidade nem rastreadores de terceiros. Você pode aceitar ou ficar só com o
            essencial. Detalhes na{" "}
            <Link
              to="/privacidade"
              hash="cookies"
              className="font-semibold text-[#0074FF] hover:underline"
            >
              Política de Privacidade
            </Link>
            .
          </p>
        </div>
        <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row">
          <Button
            variant="secondary"
            size="md"
            className="h-10 min-h-10"
            onClick={() => decide("necessary")}
          >
            Só necessários
          </Button>
          <Button
            variant="primary"
            size="md"
            className="h-10 min-h-10"
            onClick={() => decide("accepted")}
          >
            Aceitar cookies
          </Button>
        </div>
      </div>
    </section>
  );
}

export function LegalNav({ className = "" }: { className?: string }) {
  const { reopen } = useCookieConsent();

  return (
    <nav
      aria-label="Informações legais"
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.75rem] text-[var(--color-ink-muted)] ${className}`}
    >
      <Link
        to="/privacidade"
        className="transition-colors duration-[var(--dream-duration-fast)] hover:text-[var(--color-ink)] hover:underline"
      >
        Privacidade e LGPD
      </Link>
      <button
        type="button"
        className="transition-colors duration-[var(--dream-duration-fast)] hover:text-[var(--color-ink)] hover:underline"
        onClick={reopen}
      >
        Cookies
      </button>
    </nav>
  );
}
